import {
    BodyLarge,
    ButtonRowLeft,
    Flex,
    FlexCol,
    FlexColMd,
    FlexSmAlignCenter,
    Headline4,
    ModalBackground,
    TertiaryButton,
} from 'components/Elements';
import React from 'react';
import strings from '../../strings/strings.json';
import styled from 'styled-components';
import { breakpoints, sizes, theme } from 'theme';
import useKeyboardTrap from '../../hooks/useKeyboardTrap';
import useWindowWidth from '../../hooks/useWindowWidth';
import CloseIcon from '../../assets/icons/controls/CloseIcon';
import { IconSize } from '../../assets/icons/icon-sizes';
import Notification, { NotificationType } from '../notifiers/Notification';
import TextField from 'components/forms/TextField';
import Button from 'components/buttons/Button';

export type CopyLinkModalProps = {
    title: string;
    description?: string;
    inputValue: string;
    primaryButtonTitle: string;
    primaryButtonAria: string;
    closeButtonAria: string;
    onClickCloseButton: () => void;
};

export type CopyLinkModalState = {
    modalProps: CopyLinkModalProps;
    modalOpen: boolean;
};

export function CopyLinkModal({
    title,
    description,
    inputValue,
    primaryButtonTitle,
    primaryButtonAria,
    closeButtonAria,
    onClickCloseButton,
}: CopyLinkModalProps): JSX.Element {
    const { ref } = useKeyboardTrap(onClickCloseButton);
    const { currentWindowWidth } = useWindowWidth();

    const [showSaveNotifier, setShowSaveNotifier] = React.useState(false);

    const onClickPrimaryButton = () => {
        navigator.clipboard.writeText(inputValue);
        setShowSaveNotifier(true);
    };

    return (
        <ModalBackground>
            <ModalBody ref={ref}>
                <ModalCard>
                    <FlexColMd>
                        <RowPushItemsApart>
                            <Headline4>{title}</Headline4>
                            <TertiaryButton onClick={onClickCloseButton} aria-label={closeButtonAria}>
                                <IconContainer>
                                    <CloseIcon size={IconSize.SMALL} />
                                </IconContainer>
                            </TertiaryButton>
                        </RowPushItemsApart>
                        {description && <BodyLarge>{description}</BodyLarge>}
                        <TextField
                            fieldName="url"
                            labelText={strings.copyLinkModal.labelText}
                            inputAria={strings.copyLinkModal.copyLinkButtonModalTitleAria}
                            value={inputValue}
                            readOnly
                        />
                        {currentWindowWidth <= parseInt(breakpoints.sm) && showSaveNotifier && (
                            <Notification
                                notificationType={NotificationType.SUCCESS}
                                title={strings.copyLinkModal.copiedText}
                            />
                        )}
                        <ButtonRowLeftExtraSmallGap>
                            <Button
                                title={primaryButtonTitle}
                                aria={primaryButtonAria}
                                onClick={onClickPrimaryButton}
                            />
                            {currentWindowWidth > parseInt(breakpoints.sm) && showSaveNotifier && (
                                <Notification
                                    notificationType={NotificationType.SUCCESS}
                                    title={strings.copyLinkModal.copiedText}
                                />
                            )}
                        </ButtonRowLeftExtraSmallGap>
                    </FlexColMd>
                </ModalCard>
            </ModalBody>
        </ModalBackground>
    );
}

const ModalBody = styled(Flex)`
    align-items: center;
    width: 100%;
    height: 100%;
    margin: auto;
    max-width: 800px;
`;

const ModalCard = styled(FlexCol)`
    flex-grow: 1;
    width: 100%;
    min-height: 320px;
    justify-content: center;
    padding: ${sizes.spacingLg};
    border: solid 1px ${theme.cardBorder};
    background-color: ${theme.cardBackground};
    border-radius: ${sizes.borderRadiusLg};
    @media (max-width: ${breakpoints.sm}) {
        padding-top: 130px;
        height: 100%;
        border: none;
        justify-content: space-between;
    }
`;

const IconContainer = styled.div`
    padding: ${sizes.spacingSm};
`;

const ButtonRowLeftExtraSmallGap = styled(ButtonRowLeft)`
    height: 74px;
    gap: ${sizes.spacingMd};
    align-items: center;
    @media (max-width: ${breakpoints.sm}) {
        flex-direction: column-reverse;
    }
`;

const RowPushItemsApart = styled(FlexSmAlignCenter)`
    justify-content: space-between;
    width: 100%;
`;
